// modernTheme.js
import { createTheme, alpha } from '@mui/material/styles'

// Color palette - Modern Dashboard'a göre optimize edilmiş
const colors = {
  // Primary - Emerald Green
  emerald: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981', // Ana renk
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  // Accent - Cyan
  cyan: {
    300: '#67e8f9',
    400: '#22d3ee',
    500: '#06b6d4',
    600: '#0891b2',
  },
  // Status colors
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  // Neutrals - Deep Dark
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
}

// Helper function - Create alpha colors
const createAlpha = (color, opacity) => alpha(color, opacity)

const modernTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.emerald[500],
      light: colors.emerald[400],
      dark: colors.emerald[600],
      contrastText: '#000000',
    },
    secondary: {
      main: colors.cyan[400],
      light: colors.cyan[300],
      dark: colors.cyan[500],
      contrastText: '#000000',
    },
    success: {
      main: colors.success,
      light: colors.emerald[400],
      dark: colors.emerald[700],
      contrastText: '#000000',
    },
    error: {
      main: colors.error,
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#ffffff',
    },
    warning: {
      main: colors.warning,
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#000000',
    },
    info: {
      main: colors.info,
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    background: {
      default: colors.slate[900],
      paper: createAlpha(colors.slate[800], 0.8),
      hover: createAlpha(colors.emerald[500], 0.08),
      active: createAlpha(colors.emerald[500], 0.16),
    },
    text: {
      primary: '#ffffff',
      secondary: createAlpha('#ffffff', 0.7),
      disabled: createAlpha('#ffffff', 0.5),
    },
    divider: createAlpha(colors.emerald[500], 0.2),
    // Claude Design (Profile Settings.dc.html) navy-teal tokens, shared app-wide
    designTokens: {
      card: '#111d2c',
      cardBorder: '#1e2d40',
      field: '#0c1622',
      fieldBorder: '#1e2d40',
      label: '#64748b',
      value: '#e2e8f0',
      muted: '#64748b',
      accent: '#34d399',
      chipBg: '#16273a',
      chipBorder: '#223349',
      chipText: '#7dd3b8',
      gradient: 'linear-gradient(120deg, #22c55e 0%, #10b981 60%, #14b8a6 100%)',
      gradientBtn: 'linear-gradient(120deg, #22c55e, #10b981)',
      onGradient: '#06210f',
    },
    action: {
      active: createAlpha(colors.emerald[500], 0.54),
      hover: createAlpha(colors.emerald[500], 0.08),
      selected: createAlpha(colors.emerald[500], 0.16),
      disabled: createAlpha('#ffffff', 0.26),
      disabledBackground: createAlpha('#ffffff', 0.12),
    },
  },

  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    
    // Headlines
    h1: {
      fontWeight: 900,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 900,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 900,
      fontSize: '2.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 800,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    h6: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    
    // Body text
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0',
    },
    
    // Buttons & UI
    button: {
      fontWeight: 700,
      fontSize: '0.95rem',
      lineHeight: 1.75,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
    
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0.03em',
    },
    
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 2,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: 2,
  },

  shadows: [
    'none',
    `0 2px 4px ${createAlpha('#000', 0.1)}`,
    `0 4px 8px ${createAlpha('#000', 0.12)}`,
    `0 8px 16px ${createAlpha('#000', 0.14)}`,
    `0 12px 24px ${createAlpha('#000', 0.16)}`,
    `0 16px 32px ${createAlpha('#000', 0.18)}`,
    `0 20px 40px ${createAlpha('#000', 0.2)}`,
    `0 24px 48px ${createAlpha('#000', 0.22)}`,
    `0 28px 56px ${createAlpha('#000', 0.24)}`,
    `0 32px 64px ${createAlpha('#000', 0.26)}`,
    // Emerald glows
    `0 0 20px ${createAlpha(colors.emerald[500], 0.3)}`,
    `0 0 30px ${createAlpha(colors.emerald[500], 0.3)}`,
    `0 0 40px ${createAlpha(colors.emerald[500], 0.3)}`,
    `0 10px 30px ${createAlpha(colors.emerald[500], 0.2)}`,
    `0 15px 40px ${createAlpha(colors.emerald[500], 0.25)}`,
    `0 20px 50px ${createAlpha(colors.emerald[500], 0.3)}`,
    `0 25px 60px ${createAlpha(colors.emerald[500], 0.35)}`,
    `0 30px 70px ${createAlpha(colors.emerald[500], 0.4)}`,
    `0 35px 80px ${createAlpha(colors.emerald[500], 0.45)}`,
    `0 40px 90px ${createAlpha(colors.emerald[500], 0.5)}`,
    `0 45px 100px ${createAlpha(colors.emerald[500], 0.55)}`,
    `0 50px 110px ${createAlpha(colors.emerald[500], 0.6)}`,
    `0 55px 120px ${createAlpha(colors.emerald[500], 0.65)}`,
    `0 60px 130px ${createAlpha(colors.emerald[500], 0.7)}`,
    `0 65px 140px ${createAlpha(colors.emerald[500], 0.75)}`,
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: `linear-gradient(135deg, ${colors.slate[900]} 0%, ${colors.slate[800]} 100%)`,
          minHeight: '100vh',
          scrollBehavior: 'smooth',
          // Custom scrollbar
          '&::-webkit-scrollbar': {
            width: '14px',
            height: '14px',
          },
          '&::-webkit-scrollbar-track': {
            background: colors.slate[900],
          },
          '&::-webkit-scrollbar-thumb': {
            background: createAlpha(colors.emerald[500], 0.3),
            borderRadius: '6px',
            border: `3px solid ${colors.slate[900]}`,
            backgroundClip: 'padding-box',
            '&:hover': {
              background: createAlpha(colors.emerald[500], 0.5),
              backgroundClip: 'padding-box',
            },
          },
          // Üst/alttaki ok butonlarını ve köşeyi gizle (beyaz kutular bunlardı)
          '&::-webkit-scrollbar-button': {
            display: 'none',
            width: 0,
            height: 0,
          },
          '&::-webkit-scrollbar-corner': {
            background: 'transparent',
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
        '::selection': {
          backgroundColor: createAlpha(colors.emerald[500], 0.3),
          color: '#ffffff',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontWeight: 700,
          fontSize: '0.95rem',
          textTransform: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 10px 30px ${createAlpha(colors.emerald[500], 0.4)}`,
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.emerald[500]} 0%, ${colors.emerald[400]} 100%)`,
          color: '#000000',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.emerald[600]} 0%, ${colors.emerald[500]} 100%)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${colors.cyan[400]} 0%, ${colors.cyan[300]} 100%)`,
          color: '#000000',
        },
        outlined: {
          borderWidth: '1px',
          borderColor: createAlpha(colors.emerald[500], 0.3),
          color: colors.emerald[500],
          '&:hover': {
            borderColor: colors.emerald[500],
            backgroundColor: createAlpha(colors.emerald[500], 0.1),
            borderWidth: '1px',
          },
        },
        text: {
          color: colors.emerald[500],
          '&:hover': {
            backgroundColor: createAlpha(colors.emerald[500], 0.1),
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.85rem',
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: createAlpha(colors.emerald[500], 0.1),
            transform: 'scale(1.05)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${createAlpha(colors.slate[800], 0.8)} 0%, ${createAlpha(colors.slate[800], 0.8)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.2)}`,
          borderRadius: 16,
          boxShadow: `0 8px 32px ${createAlpha('#000', 0.3)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          // '&:hover': {
          //   borderColor: createAlpha(colors.emerald[500], 0.5),
          //   boxShadow: `0 15px 40px ${createAlpha(colors.emerald[500], 0.2)}`,
          //   transform: 'translateY(-4px)',
          // },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: createAlpha(colors.slate[800], 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.2)}`,
        },
        elevation1: {
          boxShadow: `0 2px 8px ${createAlpha('#000', 0.1)}`,
        },
        elevation2: {
          boxShadow: `0 4px 16px ${createAlpha('#000', 0.15)}`,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: createAlpha('#000', 0.3),
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': {
              borderColor: createAlpha(colors.emerald[500], 0.3),
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '&:hover fieldset': {
              borderColor: createAlpha(colors.emerald[500], 0.5),
            },
            '&.Mui-focused': {
              backgroundColor: createAlpha('#000', 0.4),
              '& fieldset': {
                borderColor: colors.emerald[500],
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
        input: {
          '&::placeholder': {
            color: createAlpha('#ffffff', 0.5),
            opacity: 1,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: createAlpha('#ffffff', 0.7),
          '&.Mui-focused': {
            color: colors.emerald[500],
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 8,
        },
        filled: {
          backgroundColor: createAlpha(colors.emerald[500], 0.1),
          color: colors.emerald[500],
          border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
        },
        outlined: {
          borderColor: createAlpha(colors.emerald[500], 0.5),
          color: colors.emerald[500],
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: createAlpha(colors.slate[900], 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
          fontSize: '0.85rem',
          padding: '8px 12px',
          borderRadius: 8,
        },
        arrow: {
          color: createAlpha(colors.slate[900], 0.95),
          '&::before': {
            border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: createAlpha(colors.slate[900], 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
          boxShadow: `0 8px 32px ${createAlpha(colors.emerald[500], 0.1)}`,
          borderRadius: 12,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: createAlpha(colors.emerald[500], 0.1),
          },
          '&.Mui-selected': {
            backgroundColor: createAlpha(colors.emerald[500], 0.2),
            '&:hover': {
              backgroundColor: createAlpha(colors.emerald[500], 0.3),
            },
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: createAlpha(colors.emerald[500], 0.5),
          '&.Mui-checked': {
            color: colors.emerald[500],
          },
          '&:hover': {
            backgroundColor: createAlpha(colors.emerald[500], 0.08),
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              color: colors.emerald[500],
              '& + .MuiSwitch-track': {
                backgroundColor: colors.emerald[500],
                opacity: 0.5,
              },
            },
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: createAlpha(colors.emerald[500], 0.2),
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: createAlpha(colors.emerald[500], 0.1),
          color: colors.emerald[500],
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: createAlpha('#ffffff', 0.1),
          borderRadius: 4,
        },
        bar: {
          backgroundColor: colors.emerald[500],
          borderRadius: 4,
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: createAlpha('#ffffff', 0.05),
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${createAlpha(colors.emerald[500], 0.1)}, transparent)`,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(135deg, ${createAlpha(colors.slate[900], 0.95)} 0%, ${createAlpha(colors.slate[800], 0.95)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
          borderRadius: 16,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: createAlpha(colors.slate[900], 0.9),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${createAlpha(colors.emerald[500], 0.2)}`,
          boxShadow: 'none',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: createAlpha(colors.slate[900], 0.95),
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${createAlpha(colors.emerald[500], 0.2)}`,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        icon: {
          color: colors.emerald[500],
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: createAlpha(colors.slate[900], 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${createAlpha(colors.emerald[500], 0.3)}`,
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: createAlpha(colors.emerald[500], 0.2),
          },
          '&.Mui-focused': {
            backgroundColor: createAlpha(colors.emerald[500], 0.1),
          },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: colors.emerald[500],
          color: '#000000',
          fontWeight: 700,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        filledSuccess: {
          backgroundColor: colors.emerald[500],
          color: '#000000',
        },
        filledError: {
          backgroundColor: colors.error,
          color: '#ffffff',
        },
        filledWarning: {
          backgroundColor: colors.warning,
          color: '#000000',
        },
        filledInfo: {
          backgroundColor: colors.info,
          color: '#ffffff',
        },
      },
    },
  },
})

export default modernTheme